"use client";

import React from "react";
import Link from "next/link";
import { PRICING_CONFIG } from "./constants";
import { PricingPlan } from "./types";

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4 md:h-5 md:w-5 shrink-0 mt-0.5"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function BadgeComponent({
  text,
  variant,
}: {
  text: string;
  variant: "popular" | "coming-soon";
}) {
  const isPopular = variant === "popular";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 md:px-3 text-xs font-semibold md:font-medium ${
        isPopular
          ? "bg-accent text-text-primary"
          : "bg-[#E8E6E3] text-[#6B6B6B]"
      }`}
    >
      {text}
    </span>
  );
}

import { useSession } from "next-auth/react";
import { useCheckoutMutation } from "@/hooks/api/useSubscription";
import { toast } from "@/lib/toast";

function PricingCard({
  plan,
  isPopular,
}: {
  plan: PricingPlan;
  isPopular: boolean;
}) {
  const { data: session, status: sessionStatus } = useSession();
  const checkoutMutation = useCheckoutMutation();

  const handleCheckout = async (e: React.MouseEvent) => {
    if (sessionStatus !== "authenticated") {
      // Allow default Link navigation to /signup
      return;
    }

    e.preventDefault();
    try {
      const result = await checkoutMutation.mutateAsync();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        toast.error("Failed to initiate checkout. Please try again.");
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      toast.error(message);
    }
  };

  const isAuthenticated = sessionStatus === "authenticated";
  const isLoading = checkoutMutation.isPending;

  // Render CTA button
  const renderCTA = () => {
    if (!isPopular) {
      return (
        <button className="mt-4 md:mt-6 flex h-11 md:h-12 w-full items-center justify-center rounded-lg border border-[#E8E6E3] bg-white text-sm md:text-[15px] font-semibold text-[#6B6B6B] transition-colors hover:bg-[#F5F4F2]">
          {plan.cta.label}
        </button>
      );
    }

    const subStatus = session?.user?.subscriptionStatus;

    let btnLabel = plan.cta.label;
    let btnHref = plan.cta.href;
    let clickHandler: ((e: React.MouseEvent) => void) | undefined = undefined;

    if (isLoading) {
      btnLabel = "Redirecting...";
    } else if (isAuthenticated) {
      if (subStatus === "TRIALING") {
        btnLabel = "Manage Subscription";
        btnHref = "/settings#billing";
      } else if (subStatus === "ACTIVE") {
        btnLabel = "Go to Dashboard";
        btnHref = "/dashboard";
      } else {
        // EXPIRED, CANCELED_AT_PERIOD_END, CANCELED, PAST_DUE, or null/undefined
        btnLabel = "Renew Plan";
        clickHandler = (e) => void handleCheckout(e);
      }
    }

    return (
      <Link
        href={btnHref}
        onClick={clickHandler}
        className={`mt-4 md:mt-6 flex h-11 md:h-12 w-full items-center justify-center rounded-lg bg-primary text-sm md:text-[15px] font-semibold text-white transition-all hover:brightness-95 hover:ring-4 hover:ring-ring ${
          isLoading ? "pointer-events-none opacity-80" : ""
        }`}
      >
        {isLoading && (
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {btnLabel}
      </Link>
    );
  };

  return (
    <div
      className={`relative rounded-2xl p-5 md:p-8 ${
        isPopular
          ? "border-2 border-accent bg-white shadow-[0_4px_24px_rgba(196,244,100,0.15)]"
          : "border border-[#e3e5e8] bg-section"
      }`}
    >
      {/* Badge  */}
      {plan.badge && (
        <div className="absolute -top-3 left-4 md:left-6">
          <BadgeComponent text={plan.badge.text} variant={plan.badge.variant} />
        </div>
      )}

      <div className="pt-1 md:pt-2">
        {/* Plan name */}
        <h2
          className={`text-base md:text-lg font-semibold ${
            isPopular ? "text-[#0F0F0F]" : "text-[#6B6B6B]"
          }`}
        >
          {plan.name}
        </h2>

        {/* Price */}
        <div className="mt-3 md:mt-4 flex items-baseline">
          <span
            className={`text-4xl md:text-5xl font-semibold tracking-tight ${
              isPopular ? "text-[#0F0F0F]" : "text-[#6B6B6B]"
            }`}
          >
            £{plan.price}
          </span>
          <span
            className={`ml-1.5 md:ml-2 text-sm md:text-base ${
              isPopular ? "text-[#6B6B6B]" : "text-[#9CA3AF]"
            }`}
          >
            {plan.period}
          </span>
        </div>

        {/* Trial */}
        {plan.trialText ? (
          <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-[#6B6B6B]">
            {plan.trialText}
          </p>
        ) : (
          <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-[#9CA3AF]">
            &nbsp;
          </p>
        )}

        {/* Description */}
        <p
          className={`mt-3 md:mt-4 text-sm md:text-[15px] leading-relaxed ${
            isPopular ? "text-[#0F0F0F]" : "text-[#6B6B6B]"
          }`}
        >
          {plan.description}
        </p>

        {/* CTA Button */}
        {renderCTA()}

        {/* CTA */}
        <p
          className={`mt-2 md:mt-3 text-center text-xs ${
            isPopular ? "text-[#6B6B6B]" : "text-[#9CA3AF]"
          }`}
        >
          {plan.cta.subtext}
        </p>

        {/* Features */}
        <ul
          className={`mt-5 md:mt-8 space-y-2 md:space-y-3 border-t pt-4 md:pt-6 ${
            isPopular ? "border-[#E8E6E3]" : "border-[#E8E6E3]"
          }`}
        >
          {plan.features.map((feature) => (
            <li key={feature.id} className="flex items-start gap-2 md:gap-3">
              <div className={isPopular ? "text-[#0F0F0F]" : "text-[#9CA3AF]"}>
                <CheckIcon />
              </div>
              <span
                className={`text-xs md:text-sm ${
                  isPopular ? "text-[#0F0F0F]" : "text-[#6B6B6B]"
                }`}
              >
                {feature.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function Pricing() {
  const { data: session, status: sessionStatus } = useSession();
  const checkoutMutation = useCheckoutMutation();
  const showNoSubscriptionBanner =
    sessionStatus === "authenticated" &&
    session?.user?.hasSubscription === false;

  const handleCheckout = async () => {
    try {
      const result = await checkoutMutation.mutateAsync();
      if (result?.url) {
        window.location.href = result.url;
      } else {
        toast.error("Failed to initiate checkout. Please try again.");
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      toast.error(message);
    }
  };

  const isLoading = checkoutMutation.isPending;

  return (
    <section className="pt-10 pb-28 md:pt-24 md:pb-36">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-[28px] md:text-5xl lg:text-6xl font-medium tracking-[-0.02em] text-text-primary">
            {PRICING_CONFIG.title}
          </h1>
          <p className="mt-3 md:mt-5 text-base md:text-xl text-text-secondary">
            {PRICING_CONFIG.subtitle}
          </p>

          {/* Early adopter note */}
          <div className="mt-5 md:mt-8 inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-2 md:px-5 md:py-2.5 border border-accent/30">
            <span className="h-1.5 w-1.5 md:h-2 md:w-2 rounded-full bg-accent" />
            <p className="text-xs md:text-sm font-medium text-text-primary">
              {PRICING_CONFIG.earlyAdopterNote}
            </p>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="mt-8 md:mt-16 grid gap-4 md:gap-6 lg:grid-cols-3 items-start max-w-5xl mx-auto">
          {PRICING_CONFIG.plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              isPopular={plan.id === "starter"}
            />
          ))}
        </div>
      </div>

      {/* {showNoSubscriptionBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-t border-accent/40 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] py-4 md:py-5 animate-slideUp">
          <div className="mx-auto max-w-7xl px-4 md:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5 p-2 bg-accent/25 rounded-lg text-text-primary">
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="space-y-0.5">
                <h4 className="text-sm font-semibold text-text-primary">
                  {session?.user?.subscriptionStatus ? "Action required: Renew your subscription" : "Action required: Select a subscription plan"}
                </h4>
                <p className="text-xs text-text-secondary leading-normal max-w-2xl">
                  {session?.user?.subscriptionStatus 
                    ? "To reactivate your Repayas dashboard, please renew your subscription." 
                    : "To access your Repayas dashboard, please activate the Starter plan. You'll get a 14-day free trial and you can cancel anytime."}
                </p>
              </div>
            </div>
            
            <button
              onClick={() => void handleCheckout()}
              disabled={isLoading}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-primary px-6 text-sm font-semibold text-white transition-all hover:brightness-95 hover:ring-4 hover:ring-ring shrink-0 w-full sm:w-auto"
            >
              {isLoading && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {isLoading ? "Redirecting..." : (session?.user?.subscriptionStatus ? "Renew Plan" : "Start 14-day Free Trial")}
            </button>
          </div>
        </div>
      )} */}
    </section>
  );
}
